/**
 * TestAI Component
 * Main content for Test AI page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { TestInputForm } from './TestInputForm';
import { StepsReview } from './StepsReview';
import { CodeViewer } from './CodeViewer';
import { Toast } from './Toast';
import { ArrowLeft, Menu } from 'lucide-react';
import { SidebarRef } from './Sidebar';

type TestAIProps = {
  sidebarRef?: React.RefObject<SidebarRef | null>;
};

export function TestAI({ sidebarRef }: TestAIProps) {
  const navigate = useNavigate();
  const { state } = useApp();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Check if test is completed (executed or code_ready)
  const isTestCompleted = state.status === 'executed' || state.status === 'code_ready';
  
  // Show toast when execution is successful
  useEffect(() => {
    if (state.status === 'executed' && state.executionResult) {
      if (state.executionResult.success) {
        setToast({ message: 'Test executed successfully!', type: 'success' });
      } else {
        setToast({ message: `Test execution failed: ${state.executionResult.error || 'Unknown error'}`, type: 'error' });
      }
    }
  }, [state.status, state.executionResult]);
  
  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Save test case');
  };
  
  const handleCancel = () => {
    // TODO: Implement cancel functionality
    navigate('/dashboard');
  };

  return (
    <div className="flex flex-col h-full testAiComponent">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="testAiHeader flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3 testAiHeaderTitle">
          {/* Mobile Menu Button - only visible on small screens */}
          <button
            onClick={() => {
              sidebarRef?.current?.openSidebar();
            }}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-base sm:text-lg">Add new test case</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={handleCancel}
            className="testAiHeaderCancel px-3 sm:px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex-1 sm:flex-initial"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isTestCompleted}
            className="testAiHeaderSave px-3 sm:px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex-1 sm:flex-initial"
          >
            Save
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content-row flex flex-col lg:flex-row flex-1 overflow-hidden">
        <div className="left-column w-full lg:w-[500px] lg:min-w-[400px] border-b lg:border-b-0 lg:border-r border-border flex flex-col relative">
          {/* Steps Section - Above textarea */}
          <div className="flex-1 overflow-y-auto">
            <StepsReview />
          </div>
          
          {/* Textarea Section - Sticky at bottom */}
          <div className="sticky bottom-0 bg-white border-t lg:border-t-0 p-4">
            <TestInputForm />
          </div>
        </div>
        <div className="flex-1 flex flex-col right-column min-h-0">
          <CodeViewer />
        </div>
      </div>
    </div>
  );
}
